import { ChangeDetectorRef, Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule, MatSelectionList } from '@angular/material/list';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabChangeEvent, MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { IgxGridModule } from '@infragistics/igniteui-angular';
import { firstValueFrom } from 'rxjs';
import {
  ICompaniesShortList,
  IDefaultGbGbCreationPrelimRequest,
  IGBFinalizeRequest,
  IInvoiceSystemGenerationDefaults,
  IParkerActiveListForInvoiceGeneration,
  IParkerProfileDetail,
  IPrelimInvoiceResponse,
} from 'src/app/shared/interfaces';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { DataService } from 'src/app/core/services/data/data.service';
import { GeneralDialogOkComponent } from 'src/app/core/dialog/general-dialog-ok/general-dialog-ok.component';
import { GenericConfirmationDialogComponent } from 'src/app/core/dialog/generic-confirmation-dialog/generic-confirmation-dialog.component';
import { MultiMonthFutureComponent } from 'src/app/invoice/general-billing/multi-month-future/multi-month-future.component';
import { MultiMonthPastComponent } from 'src/app/invoice/general-billing/multi-month-past/multi-month-past.component';
import { GbDefaultConfigurationComponent } from 'src/app/invoice/general-billing/gb-default-configuration/gb-default-configuration.component';
import { GbDefaultCurrentMonthComponent } from 'src/app/invoice/general-billing/gb-default-current-month/gb-default-current-month.component';
import { ProcessingBannerComponent } from 'src/app/shared/ui/processing-banner/processing-banner.component';
import { spaceArCoreHttpApi } from 'src/app/shared/code/libraries/http-data/spaceArHttpApi';

type IPrelimInvoiceGridRow = IPrelimInvoiceResponse & {
  finalizeStatus: boolean;
  finalizeErrorType?: 'NotFound' | 'ServerError';
};

@Component({
  selector: 'app-gb-generation',
  standalone: true,
  imports: [
    FormsModule,
    MultiMonthPastComponent,
    MultiMonthFutureComponent,
    GbDefaultConfigurationComponent,
    GbDefaultCurrentMonthComponent,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
    MatProgressBarModule,
    MatFormFieldModule,
    MatInputModule,
    MatListModule,
    MatSelectModule,
    MatCheckboxModule,
    MatSnackBarModule,
    IgxGridModule,
    ProcessingBannerComponent,
  ],
  templateUrl: './gb-generation.component.html',
  styleUrl: './gb-generation.component.scss',
})
export class GbGenerationComponent implements OnInit {
  displayPrelimInvoices = false;
  displayPreliminaryInvoices = false;
  prelimInvoiceList: IPrelimInvoiceGridRow[] = [];
  isProcessing = false;
  invoiceLineDescription = '';
  parkersWithExpiredRates: IParkerProfileDetail[] = [];
  isInvoiceLineDescriptionCustomizationEnabled = false;
  companyList: ICompaniesShortList[] = [];
  parkerList: IParkerActiveListForInvoiceGeneration[] = [];
  currentSelectedCompanyId = '';
  selectedMonths = 1;
  isSelectAllParkersChecked = false;
  isParkerSelectionReadOnly = false;
  isFinalizing = false;
  lotNumberResetToken = 0;
  resetPrelimButtonToken = 0;
  cancelGenerationToken = 0;
  selectedTabIndex = 0;
  currentInvoiceNumberFinalizing = '';
  currentCustomerNameFinalizing = '';
  private lastConfirmedTabIndex = 0;
  private isRevertingTabSelection = false;
  private isPrelimGenerationActive = false;
  invoiceSystemDefaults: IInvoiceSystemGenerationDefaults = undefined as any;
  private isInvoiceLineCustomizationStatusLoading = false;
  private lastInvoiceLineCustomizationStatusLotNumber = '';
  private isCompanyListLoading = false;
  private lastCompanyListLotNumber = '';
  private lastInvoiceSystemDefaultsLotNumber = '';
  private invoiceSystemDefaultsRequest: Promise<void> | null = null;

  @ViewChild('parkerSelectionList') parkerSelectionList?: MatSelectionList;
  @ViewChild(MatTabGroup) tabGroup?: MatTabGroup;

  dataService = inject(DataService);
  appGeneralStateService = inject(AppGeneralStateService);
  companyHttpService = inject(spaceArCoreHttpApi);
  snackBar = inject(MatSnackBar);
  changeDetectorRef = inject(ChangeDetectorRef);
  dialog = inject(MatDialog);

  ngOnInit(): void {
    this.getCompanyList(this.appGeneralStateService.currentLotNumber);
    this.setInvoiceDefaultValues(this.appGeneralStateService.currentLotNumber);
    this.getInvoiceLineCustomizationStatus(this.appGeneralStateService.currentLotNumber);
    this.getParkersByExpiredRatesByLotNumber(this.appGeneralStateService.currentLotNumber);
    this.appGeneralStateService.lotNumberSubject.subscribe({ next: (v) => this.onLotNumberChange(v) });
  }

  async onLotNumberChange(lotNumber: string): Promise<void> {
    await this.onResetInvoiceGrid(lotNumber);
    this.lotNumberResetToken++;
    this.isPrelimGenerationActive = false;
  }

  async onResetInvoiceGrid(lotNumber: string): Promise<void> {
    this.prelimInvoiceList = [];
    this.displayPrelimInvoices = false;
    this.displayPreliminaryInvoices = false;
    await this.getCompanyList(lotNumber);
    this.getInvoiceLineCustomizationStatus(lotNumber);
    this.invoiceSystemDefaults = undefined as any;
    await this.setInvoiceDefaultValues(lotNumber);
    this.getParkersByExpiredRatesByLotNumber(this.appGeneralStateService.currentLotNumber);
  }

  onEnablePrelimGenerationButton(status: boolean): void {
    this.isPrelimGenerationActive = status;
  }

  // 20260818 :: Ken Moss
  // Left for future implementation
  onCancelPrelimInvoiceGeneration(): void {
    window.alert('Cancelling the invoice generation process will stop checking the job status, but the backend job will continue running. You can check the preliminary invoices list later to see if the job completed successfully.');
  }


  async getCompanyList(lotNumber: string): Promise<void> {
    if (!lotNumber) {
      this.companyList = [];
      return;
    }
    if (this.isCompanyListLoading || this.lastCompanyListLotNumber === lotNumber) return;
    this.isCompanyListLoading = true;
    try {
      this.companyList = await firstValueFrom(this.companyHttpService.getCompanyList(lotNumber));
      this.lastCompanyListLotNumber = lotNumber;
    } catch (_err) {
      this.companyList = [];
      this.lastCompanyListLotNumber = '';
    } finally {
      this.isCompanyListLoading = false;
    }
  }

  async onCompanySelectionChange(companyId: string): Promise<void> {
    this.currentSelectedCompanyId = companyId;
    if (!companyId) {
      this.parkerList = [];
      this.isSelectAllParkersChecked = false;
      this.isParkerSelectionReadOnly = false;
      return;
    }
    setTimeout(() => {
      this.parkerSelectionList?.selectAll();
      this.isParkerSelectionReadOnly = true;
    });
  }

  onToggleSelectAllParkers(parkerSelectionList: MatSelectionList): void {
    if (this.isSelectAllParkersChecked) {
      parkerSelectionList.selectAll();
      this.isParkerSelectionReadOnly = true;
      return;
    }
    parkerSelectionList.deselectAll();
    this.isParkerSelectionReadOnly = false;
  }

  onClearPrelimInvoices(): void {
    this.prelimInvoiceList = [];
    this.displayPrelimInvoices = false;
    this.displayPreliminaryInvoices = false;
    this.resetPrelimButtonToken++;
  }

  deselectAllFinalizeStatuses(): void {
    this.prelimInvoiceList.forEach((row) => (row.finalizeStatus = false));
  }

  prepareDefaultPathPrelimInvoiceData(): IDefaultGbGbCreationPrelimRequest {
    this.prelimInvoiceList = [];
    this.displayPrelimInvoices = false;
    this.displayPreliminaryInvoices = false;
    const firstOfNextMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1);
    return {
      lotNumber: this.appGeneralStateService.currentLotNumber,
      invoiceDate: firstOfNextMonth,
      dueDate: firstOfNextMonth,
      userName: this.appGeneralStateService.userName,
      oneOffParker: false,
      tParkerId: '',
      gbLineDescription: this.invoiceLineDescription,
    };
  }

  prepareCurrentMonthPathPrelimInvoiceData(): IDefaultGbGbCreationPrelimRequest {
    const now = new Date();
    const firstOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return {
      lotNumber: this.appGeneralStateService.currentLotNumber,
      invoiceDate: firstOfCurrentMonth,
      dueDate: firstOfCurrentMonth,
      userName: this.appGeneralStateService.userName,
      oneOffParker: false,
      tParkerId: '',
      gbLineDescription: this.invoiceLineDescription,
    };
  }

  onGenerateGbDefaultPrelimInvoices(): void {
    this.isProcessing = true;
    this.dataService.GenerateGbDefaultPreliminaryInvoices(this.prepareDefaultPathPrelimInvoiceData()).subscribe({
      next: () => {
        this.isProcessing = false;
        this.snackBar.open('Invoice generation has been queued. Use the Default tab flow to track progress.', 'Close', {
          horizontalPosition: 'center', verticalPosition: 'top',
        });
      },
      error: (error) => {
        this.isProcessing = false;
        this.onErrorDuringInvoiceGeneration('');
        console.error('Error generating GB default preliminary invoices:', error);
      },
    });
  }

  onDefaultPrelimInvoiceListChange(prelimInvoiceList: IPrelimInvoiceResponse[]): void {
    if (prelimInvoiceList.length === 0) {
      this.isProcessing = false;
      if (this.isPrelimGenerationActive) this.snackBar.open('No Preliminary Invoices Generated for this Criteria', 'Close', {
        horizontalPosition: 'center', verticalPosition: 'top',
      });
      this.isPrelimGenerationActive = false;
      return;
    }
    const sortedPrelimInvoiceList = [...prelimInvoiceList].sort(
      (a, b) => new Date(a.invoiceDate as unknown as string | Date).getTime() - new Date(b.invoiceDate as unknown as string | Date).getTime(),
    );
    this.prelimInvoiceList = this.decoratePrelimInvoiceRows(sortedPrelimInvoiceList);
    this.displayPrelimInvoices = sortedPrelimInvoiceList.length > 0;
    this.displayPreliminaryInvoices = sortedPrelimInvoiceList.length > 0;
    this.isProcessing = false;
    this.isPrelimGenerationActive = false;
  }

  onDefaultPrelimProcessingStateChange(isProcessing: boolean): void {
    if (isProcessing) {
      this.prelimInvoiceList = [];
      this.displayPrelimInvoices = false;
      this.displayPreliminaryInvoices = false;
      this.isPrelimGenerationActive = true;
    }
    this.isProcessing = isProcessing;
  }

  onErrorDuringInvoiceGeneration(message: string): void {
    this.isProcessing = false;
    this.prelimInvoiceList = [];
    this.displayPrelimInvoices = false;
    this.displayPreliminaryInvoices = false;
    this.isPrelimGenerationActive = true;
    const errorMessage = message || 'An error occurred while generating preliminary invoices. Please try again. If the issue persists, contact spaceAR Support at SpaceAR@aceparking.com.';
    this.snackBar.open(errorMessage, 'Close', {
      horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'], duration: 5000,
    });
  }

  onCancelInvoiceGeneration(): void {
    this.cancelGenerationToken++;
    this.isProcessing = false;
    this.isPrelimGenerationActive = false;
  }

  onGenerateGbCurrentMonthPrelimInvoices(): void {
    this.isProcessing = true;
    this.dataService.GenerateGbDefaultPreliminaryInvoices(this.prepareCurrentMonthPathPrelimInvoiceData()).subscribe({
      next: () => {
        this.isProcessing = false;
        this.snackBar.open('Invoice generation has been queued. Use the Current Month tab flow to track progress.', 'Close', {
          horizontalPosition: 'center', verticalPosition: 'top',
        });
      },
      error: (error) => {
        this.isProcessing = false;
        console.error('Error generating GB default preliminary invoices:', error);
      },
    });
  }

  async onFinalizePrelimInvoices(): Promise<void> {
    const dialogRef = this.dialog.open(GenericConfirmationDialogComponent, {
      data: { title: 'Finalize Invoices?', line1: 'Are you sure you want to finalize the selected invoices?', line2: '', button: 'Yes' },
      width: '450px', enterAnimationDuration: '0ms', exitAnimationDuration: '0ms',
    });
    if ((await firstValueFrom(dialogRef.afterClosed())) !== true) return;
    this.isFinalizing = true;
    const invoiceNumbersToFinalize = this.prelimInvoiceList.filter((row) => row.finalizeStatus).map((row) => row.invoiceNumber);
    for (const invoiceNumber of invoiceNumbersToFinalize) {
      await new Promise<void>((resolve) => setTimeout(resolve, 1000));
      const invoice = this.prelimInvoiceList.find((row) => row.invoiceNumber === invoiceNumber);
      this.currentInvoiceNumberFinalizing = invoiceNumber;
      this.currentCustomerNameFinalizing = invoice ? invoice.customerName : '';
      const result = await this.finalizePrelimInvoiceAsync({ lotNumber: this.appGeneralStateService.currentLotNumber, invoiceNumber });
      this.prelimInvoiceList = this.prelimInvoiceList.map((row) => {
        if (row.invoiceNumber !== invoiceNumber) return row;

        if (result.success) {
          return { ...row, status: 'Final', isFinalized: true, finalizeErrorType: undefined };
        }

        const statusText = result.errorType === 'NotFound' ? 'Does Not Exist' : 'Error Finalizing';
        return { ...row, status: statusText, finalizeErrorType: result.errorType };
      });
      this.changeDetectorRef.detectChanges();
    }
    this.prelimInvoiceList = this.prelimInvoiceList.map((row) => ({ ...row, finalizeStatus: false }));
    this.isFinalizing = false;
    this.snackBar.open('All selected invoices have been finalized.', 'Close', { horizontalPosition: 'center', verticalPosition: 'top' });
  }

  get hasSelectedFinalizeRows(): boolean { return this.prelimInvoiceList.some((row) => row.finalizeStatus); }
  get selectedFinalizeRowCount(): number { return this.prelimInvoiceList.filter((row) => row.finalizeStatus).length; }
  get finalizedCount(): number { return this.prelimInvoiceList.filter((row) => row.isFinalized).length; }

  usdCurrencyFormatter = (value: any): string => value === null || value === undefined || value === '' ? '' : new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(Number(value));

  mdyDateFormatter = (value: any): string => {
    if (value === null || value === undefined || value === '') return '';
    const dateValue = new Date(value);
    if (Number.isNaN(dateValue.getTime())) return '';
    const formattedDate = `${dateValue.getMonth() + 1}-${dateValue.getDate()}-${dateValue.getFullYear()}`;
    return formattedDate === '1-1-1' ? '' : formattedDate;
  };

  onFinalizeStatusChange(row: IPrelimInvoiceGridRow, checked: boolean): void { row.finalizeStatus = checked; }
  selectAllFinalizeStatuses(): void { this.prelimInvoiceList.forEach((row) => (row.finalizeStatus = true)); }
  private decoratePrelimInvoiceRows(rows: IPrelimInvoiceResponse[]): IPrelimInvoiceGridRow[] { return rows.map((row) => ({ ...row, finalizeStatus: false })); }

  getParkersByExpiredRatesByLotNumber(lotNumber: string): void {
    this.dataService.getParkersExpiredRatesByLotNumber(lotNumber).subscribe({ next: (results) => (this.parkersWithExpiredRates = results), complete: () => {} });
  }

  getInvoiceLineCustomizationStatus(lotNumber: string): void {
    if (!lotNumber) { this.isInvoiceLineDescriptionCustomizationEnabled = false; return; }
    if (this.isInvoiceLineCustomizationStatusLoading || this.lastInvoiceLineCustomizationStatusLotNumber === lotNumber) return;
    this.isInvoiceLineCustomizationStatusLoading = true;
    this.dataService.GetGbInvoiceLineItemDescriptionStatusForLotNumber(lotNumber).subscribe({
      next: (results) => { this.isInvoiceLineDescriptionCustomizationEnabled = results; this.lastInvoiceLineCustomizationStatusLotNumber = lotNumber; },
      error: (_err: any) => { this.isInvoiceLineDescriptionCustomizationEnabled = false; this.lastInvoiceLineCustomizationStatusLotNumber = ''; },
      complete: () => { this.isInvoiceLineCustomizationStatusLoading = false; },
    });
  }

  async finalizePrelimInvoiceAsync(
    invoiceToFinalize: IGBFinalizeRequest,
  ): Promise<{ success: boolean; errorType?: 'NotFound' | 'ServerError' }> {
    try {
      await firstValueFrom(this.dataService.gb_FinalizePrelimInvoice(invoiceToFinalize));
      return { success: true };
    } catch (error: any) {
      // 400 = provided invoice number does not exist as a prelim invoice; anything else is an unexpected/server failure.
      const errorType: 'NotFound' | 'ServerError' = error?.status === 400 ? 'NotFound' : 'ServerError';
      return { success: false, errorType };
    }
  }

  onChangeInvoiceTabs(event: MatTabChangeEvent): void {
    if (this.isRevertingTabSelection) { this.isRevertingTabSelection = false; return; }
    const requestedTabIndex = event.index;
    if (this.isProcessing && requestedTabIndex !== this.lastConfirmedTabIndex) {
      this.isRevertingTabSelection = true;
      this.selectedTabIndex = this.lastConfirmedTabIndex;
      if (this.tabGroup) this.tabGroup.selectedIndex = this.lastConfirmedTabIndex;
      this.dialog.open(GeneralDialogOkComponent, { data: { dlgTitle: 'GB Generation In Process', dlgLine1: "There are GB's being generated.  You may not switch tabs until the generation process has been completed.", dlgLine2: '' } });
      return;
    }
    if (this.prelimInvoiceList.length === 0) {
      this.resetForTabSwitchWithoutDataRefetch();
      this.isPrelimGenerationActive = false;
      this.lotNumberResetToken++;
      this.isRevertingTabSelection = true;
      this.selectedTabIndex = requestedTabIndex;
      this.lastConfirmedTabIndex = requestedTabIndex;
      if (this.tabGroup) this.tabGroup.selectedIndex = requestedTabIndex;
      return;
    }
    this.isRevertingTabSelection = true;
    this.selectedTabIndex = this.lastConfirmedTabIndex;
    if (this.tabGroup) this.tabGroup.selectedIndex = this.lastConfirmedTabIndex;
    const dialogRef = this.dialog.open(GenericConfirmationDialogComponent, {
      data: { title: 'Confirm Tab Change', line1: 'Switching tabs will clear the current preliminary invoice list. Do you want to continue?', line2: '', button: 'Yes' },
      width: '450px', enterAnimationDuration: '0ms', exitAnimationDuration: '0ms',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result !== true) return;
      this.resetForTabSwitchWithoutDataRefetch();
      this.isPrelimGenerationActive = false;
      this.lotNumberResetToken++;
      this.isRevertingTabSelection = true;
      this.selectedTabIndex = requestedTabIndex;
      this.lastConfirmedTabIndex = requestedTabIndex;
      if (this.tabGroup) this.tabGroup.selectedIndex = requestedTabIndex;
    });
  }

  private resetForTabSwitchWithoutDataRefetch(): void {
    this.prelimInvoiceList = [];
    this.displayPrelimInvoices = false;
    this.displayPreliminaryInvoices = false;
    this.isProcessing = false;
  }

  onDisplayPrelimInvoice(invoiceNumber: string): void {
    this.snackBar.open(`Loading Invoice PDF for Invoice Number ${invoiceNumber}`, undefined, { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top' });
    const lotNumber = this.appGeneralStateService.currentLotNumber;
    this.dataService.GetGbInvoicePdf(invoiceNumber, lotNumber).subscribe((response) => {
      const blob = new Blob([response.body!], { type: 'application/pdf' });
      window.open(URL.createObjectURL(blob));
    });
  }

  async setInvoiceDefaultValues(lotNumber: string): Promise<void> {
    if (!lotNumber) { this.invoiceSystemDefaults = undefined as any; this.lastInvoiceSystemDefaultsLotNumber = ''; return; }
    if (this.invoiceSystemDefaultsRequest) await this.invoiceSystemDefaultsRequest;
    if (this.lastInvoiceSystemDefaultsLotNumber === lotNumber && this.invoiceSystemDefaults) return;
    this.invoiceSystemDefaultsRequest = (async () => {
      try {
        this.invoiceSystemDefaults = await firstValueFrom(this.dataService.invoiceCreation_GetSystemDefaults(lotNumber));
        this.lastInvoiceSystemDefaultsLotNumber = lotNumber;
      } catch (_err) {
        this.invoiceSystemDefaults = undefined as any;
        this.lastInvoiceSystemDefaultsLotNumber = '';
      } finally { this.invoiceSystemDefaultsRequest = null; }
    })();
    await this.invoiceSystemDefaultsRequest;
  }
}
